// CODA-1009: Public customer self-service onboarding portal (/onboarding/:token)
// CODA-1944: 2-phase portal — phase1 = Stammdaten/contacts, phase2 = documents/signature.
// No auth guard — token-based access only.
import { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { ErrorState } from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OnboardingDocument {
  id: string
  title: string
  description: string | null
  file_name: string | null
  sort_order: number
  requires_signature: boolean | number
}

interface PortalData {
  id: string
  status: string
  phase: 'phase1' | 'phase2'
  company_name: string
  company_street: string | null
  company_zip: string | null
  company_city: string | null
  company_country: string | null
  contact_first_name: string | null
  contact_last_name: string | null
  contact_email: string | null
  contact_phone: string | null
  signature_pdf_name: string | null
  documents: OnboardingDocument[]
  confirmedDocumentIds: string[]
  allowCustomerEdit: boolean
  alreadyCompleted: boolean
}

interface AdditionalContact {
  name: string
  email: string
  phone: string
  role: string
}

// ─── Shared inline styles ─────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column',
  alignItems: 'center', padding: '40px 16px',
}
const cardStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
  padding: '36px 40px', width: '100%', maxWidth: 640,
}

// ─── Step bar ─────────────────────────────────────────────────────────────────

function StepBar({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const done = i < current
        const active = i === current
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#16a34a' : active ? '#1e40af' : '#e5e7eb',
                color: done || active ? '#fff' : '#6b7280',
                fontWeight: 700, fontSize: 14, transition: 'background 0.2s',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 11, marginTop: 4, color: active ? '#1e40af' : done ? '#16a34a' : '#9ca3af', whiteSpace: 'nowrap' }}>
                {label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#16a34a' : '#e5e7eb', margin: '0 4px', marginBottom: 20 }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({ label, value, onChange, disabled, required }: {
  label: string
  value: string
  onChange?: (v: string) => void
  disabled?: boolean
  required?: boolean
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%', padding: '9px 12px', border: `1px solid ${disabled ? '#e5e7eb' : '#d1d5db'}`,
          borderRadius: 8, fontSize: 14, background: disabled ? '#f9fafb' : '#fff',
          color: disabled ? '#6b7280' : '#111827', boxSizing: 'border-box',
          outline: 'none', transition: 'border-color 0.15s',
        }}
      />
    </div>
  )
}

function Btn({ children, onClick, variant = 'primary', disabled }: {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'success'
  disabled?: boolean
}) {
  const bg = variant === 'primary' ? '#1e40af' : variant === 'success' ? '#16a34a' : '#f3f4f6'
  const color = variant === 'primary' || variant === 'success' ? '#fff' : '#374151'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? '#e5e7eb' : bg, color: disabled ? '#9ca3af' : color,
        border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
      }}
    >
      {children}
    </button>
  )
}

// ─── Phase 1 steps ────────────────────────────────────────────────────────────

function Phase1StepStammdaten({
  data,
  form,
  onChange,
  onNext,
}: {
  data: PortalData
  form: Record<string, string>
  onChange: (key: string, val: string) => void
  onNext: () => void
}) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Stammdaten prüfen</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Bitte überprüfen Sie Ihre Unternehmensdaten und korrigieren Sie diese falls nötig.
        {!data.allowCustomerEdit && (
          <span style={{ display: 'block', marginTop: 4, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 6 }}>
            Daten können in diesem Onboarding nicht bearbeitet werden. Bitte wenden Sie sich bei Korrekturen an uns.
          </span>
        )}
      </p>

      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#6b7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unternehmen</div>
        <Field label="Firmenname" value={data.company_name} disabled />
        <Field label="Straße" value={form['companyStreet'] ?? ''} onChange={(v) => onChange('companyStreet', v)} disabled={!data.allowCustomerEdit} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <Field label="PLZ" value={form['companyZip'] ?? ''} onChange={(v) => onChange('companyZip', v)} disabled={!data.allowCustomerEdit} />
          <Field label="Stadt" value={form['companyCity'] ?? ''} onChange={(v) => onChange('companyCity', v)} disabled={!data.allowCustomerEdit} />
        </div>
        <Field label="Land" value={form['companyCountry'] ?? ''} disabled />
      </div>

      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#6b7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hauptansprechpartner</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Vorname" value={form['contactFirstName'] ?? ''} onChange={(v) => onChange('contactFirstName', v)} disabled={!data.allowCustomerEdit} />
          <Field label="Nachname" value={form['contactLastName'] ?? ''} onChange={(v) => onChange('contactLastName', v)} disabled={!data.allowCustomerEdit} />
        </div>
        <Field label="E-Mail" value={data.contact_email ?? ''} disabled />
        <Field label="Telefon" value={form['contactPhone'] ?? ''} onChange={(v) => onChange('contactPhone', v)} disabled={!data.allowCustomerEdit} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={onNext}>Weiter →</Btn>
      </div>
    </div>
  )
}

function Phase1StepContacts({
  contacts,
  onChange,
  onNext,
  onBack,
}: {
  contacts: AdditionalContact[]
  onChange: (contacts: AdditionalContact[]) => void
  onNext: () => void
  onBack: () => void
}) {
  const addContact = () =>
    onChange([...contacts, { name: '', email: '', phone: '', role: '' }])

  const updateContact = (idx: number, key: keyof AdditionalContact, val: string) => {
    const updated = contacts.map((c, i) => (i === idx ? { ...c, [key]: val } : c))
    onChange(updated)
  }

  const removeContact = (idx: number) =>
    onChange(contacts.filter((_, i) => i !== idx))

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Weitere Ansprechpartner</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Optional: Fügen Sie weitere Ansprechpartner hinzu, die wir für Sie kontaktieren dürfen.
      </p>

      {contacts.map((c, i) => (
        <div key={i} style={{ background: '#f9fafb', borderRadius: 10, padding: 16, marginBottom: 12, position: 'relative' }}>
          <button
            onClick={() => removeContact(i)}
            style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 18 }}
            aria-label="Entfernen"
          >×</button>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: '#374151' }}>Ansprechpartner {i + 1}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Name" value={c.name} onChange={(v) => updateContact(i, 'name', v)} />
            <Field label="Funktion / Rolle" value={c.role} onChange={(v) => updateContact(i, 'role', v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="E-Mail" value={c.email} onChange={(v) => updateContact(i, 'email', v)} />
            <Field label="Telefon" value={c.phone} onChange={(v) => updateContact(i, 'phone', v)} />
          </div>
        </div>
      ))}

      {contacts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 14 }}>
          Keine weiteren Ansprechpartner hinterlegt.
        </div>
      )}

      <button
        onClick={addContact}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '2px dashed #d1d5db', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', color: '#6b7280', fontSize: 14, marginBottom: 24, width: '100%', justifyContent: 'center' }}
      >
        + Ansprechpartner hinzufügen
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Btn variant="secondary" onClick={onBack}>← Zurück</Btn>
        <Btn onClick={onNext}>Daten bestätigen →</Btn>
      </div>
    </div>
  )
}

function Phase1Done() {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#16a34a', marginBottom: 12 }}>Stammdaten bestätigt!</h2>
      <p style={{ color: '#374151', fontSize: 16, marginBottom: 8 }}>
        Vielen Dank für die Bestätigung Ihrer Daten.
      </p>
      <p style={{ color: '#6b7280', fontSize: 14 }}>
        Sie erhalten in Kürze eine weitere E-Mail mit dem Link zur Bestätigung Ihrer Vertragsdokumente.
      </p>
    </div>
  )
}

// ─── Phase 2 steps ────────────────────────────────────────────────────────────

function Phase2StepStammdaten({
  data,
  onNext,
}: {
  data: PortalData
  onNext: () => void
}) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Ihre Stammdaten</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Zur Information – Ihre bereits bestätigten Unternehmensdaten:
      </p>

      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#6b7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unternehmen</div>
        <Field label="Firmenname" value={data.company_name} disabled />
        <Field label="Straße" value={data.company_street ?? ''} disabled />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <Field label="PLZ" value={data.company_zip ?? ''} disabled />
          <Field label="Stadt" value={data.company_city ?? ''} disabled />
        </div>
        <Field label="Land" value={data.company_country ?? ''} disabled />
      </div>

      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#6b7280', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hauptansprechpartner</div>
        <Field label="Name" value={[data.contact_first_name, data.contact_last_name].filter(Boolean).join(' ')} disabled />
        <Field label="E-Mail" value={data.contact_email ?? ''} disabled />
        {data.contact_phone && <Field label="Telefon" value={data.contact_phone} disabled />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={onNext}>Weiter zu den Dokumenten →</Btn>
      </div>
    </div>
  )
}

function Phase2StepDocuments({
  token,
  documents,
  confirmed,
  onToggle,
  onNext,
  onBack,
}: {
  token: string
  documents: OnboardingDocument[]
  confirmed: Set<string>
  onToggle: (docId: string, checked: boolean) => void
  onNext: () => void
  onBack: () => void
}) {
  const allRequired = documents.every((d) => confirmed.has(d.id))

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Dokumente bestätigen</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Bitte lesen und bestätigen Sie alle Dokumente. Sie können jedes Dokument als PDF öffnen.
      </p>

      {documents.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>Keine Dokumente vorhanden.</div>
      )}

      {documents.map((doc) => (
        <div
          key={doc.id}
          style={{
            border: `2px solid ${confirmed.has(doc.id) ? '#16a34a' : '#e5e7eb'}`,
            borderRadius: 10, padding: 16, marginBottom: 12,
            background: confirmed.has(doc.id) ? '#f0fdf4' : '#fff',
            transition: 'border-color 0.2s, background 0.2s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <input
              type="checkbox"
              id={`doc-${doc.id}`}
              checked={confirmed.has(doc.id)}
              onChange={(e) => onToggle(doc.id, e.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }}
            />
            <div style={{ flex: 1 }}>
              <label htmlFor={`doc-${doc.id}`} style={{ fontWeight: 600, fontSize: 15, color: '#111827', cursor: 'pointer' }}>
                {doc.title}
                {doc.requires_signature && (
                  <span style={{ marginLeft: 8, fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                    Unterschrift erforderlich
                  </span>
                )}
              </label>
              {doc.description && (
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{doc.description}</p>
              )}
              {doc.file_name && (
                <a
                  href={`/api/onboarding/portal/${token}/documents/${doc.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 13, color: '#1e40af', textDecoration: 'none', fontWeight: 500 }}
                >
                  📄 {doc.file_name} öffnen
                </a>
              )}
            </div>
            {confirmed.has(doc.id) && (
              <div style={{ color: '#16a34a', fontSize: 20, flexShrink: 0 }}>✓</div>
            )}
          </div>
        </div>
      ))}

      {documents.length > 0 && !allRequired && (
        <p style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>
          Bitte bestätigen Sie alle Dokumente, bevor Sie fortfahren.
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <Btn variant="secondary" onClick={onBack}>← Zurück</Btn>
        <Btn onClick={onNext} disabled={documents.length > 0 && !allRequired}>Weiter →</Btn>
      </div>
    </div>
  )
}

function Phase2StepSignature({
  token,
  documents,
  existingSignatureName,
  onNext,
  onBack,
}: {
  token: string
  documents: OnboardingDocument[]
  existingSignatureName: string | null
  onNext: () => void
  onBack: () => void
}) {
  const needsSignature = documents.some((d) => d.requires_signature)
  const [uploaded, setUploaded] = useState(!!existingSignatureName)
  const [uploadedName, setUploadedName] = useState(existingSignatureName ?? '')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Bitte nur PDF-Dateien hochladen.')
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setUploadError('Datei zu groß (max. 15 MB).')
      return
    }
    setUploading(true)
    setUploadError(null)
    try {
      const buf = await file.arrayBuffer()
      const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)))
      await axios.post(`/api/onboarding/portal/${token}/upload`, {
        fileContentBase64: b64,
        fileName: file.name,
      })
      setUploaded(true)
      setUploadedName(file.name)
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Upload fehlgeschlagen.'
      setUploadError(msg)
    } finally {
      setUploading(false)
    }
  }

  if (!needsSignature) {
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Unterschrift</h2>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 20, marginBottom: 24, color: '#166534', fontSize: 14 }}>
          ✓ Für dieses Onboarding ist keine Unterschrift erforderlich.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Btn variant="secondary" onClick={onBack}>← Zurück</Btn>
          <Btn onClick={onNext}>Weiter →</Btn>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Unterschrift hochladen</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Bitte laden Sie Ihre Unterschrift als PDF-Datei hoch (z. B. gescannte Unterschrift oder signiertes Dokument).
        Maximale Dateigröße: 15 MB.
      </p>

      {uploaded ? (
        <div style={{ background: '#f0fdf4', border: '2px solid #16a34a', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <div style={{ fontWeight: 600, color: '#166534', marginBottom: 4 }}>✓ Datei hochgeladen</div>
          <div style={{ fontSize: 13, color: '#4b7c5d' }}>📄 {uploadedName}</div>
          <button
            onClick={() => { setUploaded(false); setUploadedName('') }}
            style={{ marginTop: 10, fontSize: 13, color: '#1e40af', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            Andere Datei hochladen
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const file = e.dataTransfer.files[0]
            if (file) handleFile(file)
          }}
          style={{
            border: '2px dashed #d1d5db', borderRadius: 10, padding: '40px 24px',
            textAlign: 'center', cursor: 'pointer', marginBottom: 24,
            background: '#fafafa', transition: 'border-color 0.15s',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>📄</div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            {uploading ? 'Wird hochgeladen …' : 'PDF hier ablegen oder klicken'}
          </div>
          <div style={{ fontSize: 13, color: '#9ca3af' }}>PDF, max. 15 MB</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
        </div>
      )}

      {uploadError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
          {uploadError}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Btn variant="secondary" onClick={onBack}>← Zurück</Btn>
        <Btn onClick={onNext} disabled={!uploaded}>Weiter →</Btn>
      </div>
    </div>
  )
}

function Phase2Done() {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: '#16a34a', marginBottom: 12 }}>Vielen Dank!</h2>
      <p style={{ color: '#374151', fontSize: 16, marginBottom: 8 }}>
        Ihr Onboarding wurde erfolgreich abgeschlossen.
      </p>
      <p style={{ color: '#6b7280', fontSize: 14 }}>
        Wir haben Ihre Angaben erhalten und werden uns zeitnah bei Ihnen melden.
        Bei Fragen stehen wir Ihnen gerne zur Verfügung.
      </p>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function OnboardingPortalPage() {
  const { token } = useParams<{ token: string }>()
  const [step, setStep] = useState(0)
  const [form, setForm] = useState<Record<string, string>>({})
  const [additionalContacts, setAdditionalContacts] = useState<AdditionalContact[]>([])
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set())
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery<PortalData>({
    queryKey: ['onboarding-portal', token],
    queryFn: () => axios.get(`/api/onboarding/portal/${token}`).then((r) => r.data as PortalData),
    enabled: !!token,
    retry: false,
  })

  useEffect(() => {
    if (!data) return
    setForm({
      companyStreet: data.company_street ?? '',
      companyZip: data.company_zip ?? '',
      companyCity: data.company_city ?? '',
      companyCountry: data.company_country ?? 'DE',
      contactFirstName: data.contact_first_name ?? '',
      contactLastName: data.contact_last_name ?? '',
      contactPhone: data.contact_phone ?? '',
    })
    if (data.confirmedDocumentIds?.length) {
      setConfirmed(new Set(data.confirmedDocumentIds))
    }
    if (data.alreadyCompleted) {
      // Jump to the "done" step for the relevant phase.
      if (data.phase === 'phase1') setStep(2)
      else setStep(data.documents?.some((d) => d.requires_signature) ? 3 : 2)
    }
  }, [data])

  const submitMutation = useMutation({
    mutationFn: () =>
      axios.post(`/api/onboarding/portal/${token}/submit`, {
        ...form,
        confirmedDocumentIds: Array.from(confirmed),
        additionalContacts,
      }),
    onSuccess: (response) => {
      const result = response.data as { phase?: string }
      if (result.phase === 'phase1') {
        setStep(2)  // phase1 done step
      } else {
        // phase2: jump to done step (accounting for signature step)
        const needsSig = (data?.documents ?? []).some((d) => d.requires_signature)
        setStep(needsSig ? 4 : 3)
      }
      setSubmitError(null)
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Fehler beim Absenden.'
      setSubmitError(msg)
    },
  })

  const handleFormChange = (key: string, val: string) =>
    setForm((prev) => ({ ...prev, [key]: val }))

  const handleDocToggle = (docId: string, checked: boolean) =>
    setConfirmed((prev) => {
      const next = new Set(prev)
      if (checked) next.add(docId)
      else next.delete(docId)
      return next
    })

  // ─── Loading / Error states ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            Lade Onboarding …
          </div>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    const errMsg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Link ungültig oder abgelaufen. Bitte wenden Sie sich an Ihren Ansprechpartner bei implec.'
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <ErrorState
            title="Zugriff nicht möglich"
            description={errMsg}
            details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error), 2) : undefined}
          />
        </div>
      </div>
    )
  }

  const needsSignature = (data.documents ?? []).some((d) => d.requires_signature)
  const phase = data.phase

  // ─── Phase 1 flow: Stammdaten → Ansprechpartner → Done ──────────────────────

  if (phase === 'phase1') {
    const phase1Steps = ['Stammdaten', 'Ansprechpartner', 'Bestätigung']

    return (
      <div style={pageStyle}>
        <div style={{ width: '100%', maxWidth: 640, marginBottom: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1e40af', letterSpacing: '-0.5px' }}>implec</div>
          <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>Kunden-Onboarding Portal – Schritt 1: Stammdaten</div>
        </div>

        <div style={cardStyle}>
          <StepBar steps={phase1Steps} current={step} />

          {step === 0 && (
            <Phase1StepStammdaten
              data={data}
              form={form}
              onChange={handleFormChange}
              onNext={() => setStep(1)}
            />
          )}

          {step === 1 && (
            <Phase1StepContacts
              contacts={additionalContacts}
              onChange={setAdditionalContacts}
              onNext={() => submitMutation.mutate()}
              onBack={() => setStep(0)}
            />
          )}

          {step === 2 && <Phase1Done />}

          {submitMutation.isPending && (
            <div style={{ marginTop: 16, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              Wird übermittelt …
            </div>
          )}

          {submitError && (
            <div style={{ marginTop: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
              {submitError}
            </div>
          )}
        </div>

        <div style={{ marginTop: 24, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
          © implec GmbH · {data.company_name}
        </div>
      </div>
    )
  }

  // ─── Phase 2 flow: Stammdaten (readonly) → Dokumente → (Unterschrift) → Done ─

  const phase2Steps = needsSignature
    ? ['Stammdaten', 'Dokumente', 'Unterschrift', 'Abschluss']
    : ['Stammdaten', 'Dokumente', 'Abschluss']

  const doneStep = needsSignature ? 3 : 2

  const handleDocsNext = () => {
    if (needsSignature) setStep(2)
    else submitMutation.mutate()
  }

  const handleSignatureNext = () => {
    submitMutation.mutate()
  }

  return (
    <div style={pageStyle}>
      <div style={{ width: '100%', maxWidth: 640, marginBottom: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#1e40af', letterSpacing: '-0.5px' }}>implec</div>
        <div style={{ fontSize: 14, color: '#6b7280', marginTop: 2 }}>Kunden-Onboarding Portal – Schritt 2: Vertragsdokumente</div>
      </div>

      <div style={cardStyle}>
        <StepBar steps={phase2Steps} current={step} />

        {step === 0 && (
          <Phase2StepStammdaten
            data={data}
            onNext={() => setStep(1)}
          />
        )}

        {step === 1 && (
          <Phase2StepDocuments
            token={token!}
            documents={data.documents ?? []}
            confirmed={confirmed}
            onToggle={handleDocToggle}
            onNext={handleDocsNext}
            onBack={() => setStep(0)}
          />
        )}

        {step === 2 && needsSignature && (
          <Phase2StepSignature
            token={token!}
            documents={data.documents ?? []}
            existingSignatureName={data.signature_pdf_name}
            onNext={handleSignatureNext}
            onBack={() => setStep(1)}
          />
        )}

        {step === doneStep && <Phase2Done />}

        {submitMutation.isPending && (
          <div style={{ marginTop: 16, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            Wird übermittelt …
          </div>
        )}

        {submitError && (
          <div style={{ marginTop: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
            {submitError}
          </div>
        )}
      </div>

      <div style={{ marginTop: 24, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
        © implec GmbH · {data.company_name}
      </div>
    </div>
  )
}
